// frontend/src/components/chat/NewMessage.tsx
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '../../store';
import { useDebounce } from '../../hooks/useDebounce';
import {
  chatKeys,
  flattenMessages,
  useDirectory,
  useMessages,
} from '../../hooks/chat/useChatQueries';
import {
  useSendMessage,
  useStartConversation,
} from '../../hooks/chat/useChatMutations';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { NEW_MESSAGE_WINDOW } from '../../store/slices/chatWindows';
import type { ChannelHeader, DirectoryRow } from '../../services/api';
import { DirectoryList } from './DirectoryList';
import { MessageList } from './MessageList';
import { SendBar } from './SendBar';
import { ToField } from './ToField';

interface NewMessageProps {
  onOpenChannel: (channelId: string) => void;
  /** Phone sheet sizing for the composer. */
  touch?: boolean;
}

/** The body of the New message window; the window draws the header. */
export function NewMessage({ onOpenChannel, touch = false }: NewMessageProps) {
  const currentUserId = useStore(state => state.user?.id ?? '');
  const showError = useStore(state => state.showError);

  // Recipients and text live in the store, so a minimize (which unmounts this
  // view) keeps them, as it keeps a conversation's draft.
  const chips = useStore(state => state.newMessageRecipients);
  const setChips = useStore(state => state.setNewMessageRecipients);
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [resolvedIds, setResolvedIds] = useState<string[]>([]);
  const draft = useStore(state => state.chatDrafts[NEW_MESSAGE_WINDOW] ?? '');
  const setChatDraft = useStore(state => state.setChatDraft);
  const setDraft = (value: string) => setChatDraft(NEW_MESSAGE_WINDOW, value);
  const [sendError, setSendError] = useState<string | null>(null);

  // The repo's useDebounce debounces a callback, not a value: the input keeps
  // `query`, and the debounced callback moves it into `searchTerm`, which is
  // what the query key reads.
  // The callbacks are passed as the stable state setters: an inline arrow would
  // make a new debounced function each render, re-run the resolve effect, and
  // set a new `resolvedIds` array every 300 ms forever.
  const debounceSearch = useDebounce(setSearchTerm, 200);
  const debounceResolve = useDebounce(setResolvedIds, 300);
  const directoryQuery = useDirectory(searchTerm);
  const userIds = chips.map(c => c.id);
  const idsKey = userIds.join(',');
  const { find, create } = useStartConversation();
  const { send } = useSendMessage();

  // Find-only lookup on every change of the To set. Nothing is created here;
  // `find` answers null on a 404, which is the empty state.
  const lookup = useQuery({
    queryKey: chatKeys.conversation(resolvedIds),
    queryFn: async () => {
      try {
        return await find(resolvedIds);
      } catch (error) {
        console.error('Chat conversation lookup failed', error);
        throw error;
      }
    },
    enabled: resolvedIds.length > 0,
    retry: false,
  });
  // The lookup lags the chips by the debounce. Until it has answered for the
  // current set, the previous set's conversation must not be shown or sent to,
  // and the empty state must not be claimed.
  const lookupIsCurrent =
    resolvedIds.length > 0 && resolvedIds.join(',') === idsKey;
  const found = lookupIsCurrent ? (lookup.data ?? null) : null;
  const lookupSettled =
    lookupIsCurrent && lookup.isFetched && !lookup.isFetching;
  const toError =
    sendError ?? (lookup.error ? getBusinessErrorMessage(lookup.error) : null);

  // `useDirectory` keeps the previous term's rows while the next term waits out
  // the debounce or is still in flight. Those rows answer text the person has
  // already replaced, so they are neither listed nor pickable: the list and the
  // Enter key must describe the same, current, text, or Enter addresses the
  // message to the first match of text that is gone.
  const directoryIsCurrent =
    query === searchTerm &&
    !directoryQuery.isPending &&
    !directoryQuery.isPlaceholderData;
  // Group rows disappear once a person is picked: a group cannot be combined
  // with people chips.
  const rows = directoryIsCurrent
    ? (directoryQuery.data ?? []).filter(
        row => row.type === 'user' || chips.length === 0
      )
    : [];
  const showDirectory = query.length > 0 || chips.length === 0;

  const messagesQuery = useMessages(found?.id ?? null);
  const messages = flattenMessages(messagesQuery.data?.pages);
  // History that did not load: say so rather than show an empty thread with a
  // live send bar, which would read as a conversation with nothing in it.
  const historyError = messagesQuery.error;

  // Re-resolve 300 ms after the last chip change.
  useEffect(() => {
    debounceResolve(idsKey ? idsKey.split(',') : []);
  }, [idsKey, debounceResolve]);

  const pick = (row: DirectoryRow) => {
    if (row.type === 'group' && row.channel_id) {
      onOpenChannel(row.channel_id);
      return;
    }
    if (!chips.some(c => c.id === row.id)) setChips([...chips, row]);
    setQuery('');
    setSearchTerm('');
  };

  // Closing the window unmounts this view, and so does a minimize. A create
  // that answers after a close (or after a later compose began) must not act
  // on the store; after a minimize the compose is the same, so the send goes
  // on without this view.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Open the conversation first, then send there: its window shows the pending
  // row, and a failed row's Retry reuses this attempt's client_id.
  const sendInChannel = (
    channelId: string,
    body: string,
    openChannel = onOpenChannel
  ) => {
    openChannel(channelId);
    send(channelId, body).catch(error => {
      // 403 and 409: `send` kept the text as the channel draft and refetched
      // the header, so the conversation window shows the notice instead of a toast.
      const status = getHttpStatus(error);
      if (status === 403 || status === 409) return;
      showError(
        'Failed to send message',
        getBusinessErrorMessage(error) ?? undefined
      );
    });
  };

  const doSend = async () => {
    const body = draft.trim();
    if (!body) return;
    setSendError(null);
    const composeId = useStore.getState().newMessageComposeId;
    const ended = () => useStore.getState().newMessageComposeId !== composeId;
    // Cleared before any request, so a second Enter has nothing to send.
    setDraft('');
    if (found) {
      sendInChannel(found.id, body);
      return;
    }
    // First send to a set with no conversation: create once, then send.
    let header: ChannelHeader;
    try {
      header = await create(userIds);
    } catch (error) {
      console.error('Chat conversation create failed', error);
      if (ended()) return;
      setDraft(body);
      const message = getBusinessErrorMessage(error);
      if (mounted.current && message) {
        setSendError(message);
        return;
      }
      showError('Failed to send message');
      return;
    }
    if (ended()) {
      // A closed compose is canceled: write nothing, send nothing.
      return;
    }
    sendInChannel(
      header.id,
      body,
      mounted.current
        ? onOpenChannel
        : id => useStore.getState().replaceChatWindow(NEW_MESSAGE_WINDOW, id)
    );
  };

  return (
    <>
      <ToField
        chips={chips}
        query={query}
        error={toError}
        onQueryChange={value => {
          setQuery(value);
          debounceSearch(value);
        }}
        onRemove={id => setChips(chips.filter(c => c.id !== id))}
        onRemoveLast={() => setChips(chips.slice(0, -1))}
        // Enter picks only a row the person can see: with chips and an empty
        // query the directory is hidden, so there is nothing to pick.
        onPickHighlighted={() => showDirectory && rows[0] && pick(rows[0])}
      />

      {showDirectory ? (
        directoryIsCurrent ? (
          <DirectoryList rows={rows} highlightedIndex={0} onPick={pick} />
        ) : (
          // No result describes the current text yet: an empty area, never
          // "No matches" and never a highlighted row from the older text.
          <div className='flex-1 overflow-y-auto' />
        )
      ) : found ? (
        historyError ? (
          <p className='flex-1 px-6 py-8 text-center text-sm text-muted-foreground'>
            Messages are not available.
          </p>
        ) : (
          <>
            <MessageList
              messages={messages}
              pending={[]}
              currentUserId={currentUserId}
              isGroup={found.kind !== 'conversation' || found.member_count > 2}
              lastReadSeq={found.last_read_seq ?? 0}
              historyReady={
                messagesQuery.isSuccess && !messagesQuery.isFetching
              }
              hasOlder={!!messagesQuery.hasNextPage}
              isLoadingOlder={messagesQuery.isFetchingNextPage}
              onLoadOlder={() => void messagesQuery.fetchNextPage()}
              onRetry={() => undefined}
            />
            <SendBar
              inputId='chat-send-new-message'
              title={found.title}
              value={draft}
              onChange={setDraft}
              onSend={() => void doSend()}
              touch={touch}
            />
          </>
        )
      ) : (
        <>
          <div className='flex-1 overflow-y-auto'>
            {lookupSettled && (
              <p className='px-3 py-6 text-center text-xs text-muted-foreground'>
                No messages yet
              </p>
            )}
          </div>
          <SendBar
            inputId='chat-send-new-message'
            title={chips.map(c => c.label).join(', ')}
            value={draft}
            onChange={setDraft}
            onSend={() => void doSend()}
            touch={touch}
          />
        </>
      )}
    </>
  );
}
